<script setup lang="ts">
import { mergeRegister } from "@lexical/utils";
import { type LexicalNode, type RangeSelection } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import { EVENT_GENERATE_RECOMMENDATION } from "~/components/text/const";
import { getRecommendationTypeaheadSections } from "~/components/text/utils";
import { Language, RecommendationKind } from "~/shared/enums";
import type { TypeaheadGetOptionsFn, TypeaheadOption } from "~/shared/types";
import { usePageStore } from "~/stores";
import { isLexicalStateEmpty } from "~/utils/common";

import TypeaheadPlugin from "./TypeaheadPlugin.vue";

const props = defineProps<{
  title: string;
}>();

const pageStore = usePageStore();
const editor = useLexicalComposer();

const editorEmpty = ref(false);

const getOptionsFns = computed<TypeaheadGetOptionsFn[]>(() => {
  const titleEmpty = props.title.trim() === "";
  const sections = getRecommendationTypeaheadSections(editor._config.namespace, true, editorEmpty.value, titleEmpty);
  return sections.map((section) => () => Promise.resolve(section));
});

const onSelect = (_: RangeSelection, option: TypeaheadOption, parentNode: LexicalNode | null) => {
  if (option.disabled) {
    return;
  }
  editor.dispatchCommand(EVENT_GENERATE_RECOMMENDATION, {
    kind: option.value as RecommendationKind | Language,
    parentKey: parentNode?.getKey() ?? null,
  });
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        editorEmpty.value = isLexicalStateEmpty(editorState.toJSON(), "/");
      });
    })
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <TypeaheadPlugin
    v-if="pageStore.isOnline"
    trigger="/"
    :get-options-fns="getOptionsFns"
    :wide="pageStore.isMobile"
    @select="onSelect" />
</template>
